import React, { useState, useEffect } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { apiClient } from "../services/api";
import { Client, ClientSummary } from "../types/database";
import "./Clients.css";

interface ClientWithSummary extends Client {
  summary?: ClientSummary;
}

export default function Clients() {
  const [clients, setClients] = useState<ClientWithSummary[]>([]);
  const [activeClients, setActiveClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [generatingSummary, setGeneratingSummary] = useState<string | null>(
    null
  );
  const [pollingSummary, setPollingSummary] = useState<string | null>(null);
  const [downloadingSummary, setDownloadingSummary] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Auto-dismiss success message after 2 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (!pollingSummary) return;

    const interval = setInterval(async () => {
      console.log(`🔄 Polling summary status for client: ${pollingSummary}`);
      const summary = await apiClient.getClientSummary(pollingSummary);

      if (
        summary &&
        (summary.generation_status === "completed" ||
          summary.generation_status === "failed")
      ) {
        console.log(
          `✅ Polling complete for client ${pollingSummary}, status: ${summary.generation_status}`
        );
        setPollingSummary(null); // Stop polling
        refreshClientsAndSummaries(); // Final refresh
      } else {
        // Keep polling, maybe refresh the UI partially
        loadClientSummary(pollingSummary);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [pollingSummary]);

  // Load summaries for clients that might have them
  useEffect(() => {
    if (clients.length > 0) {
      // Load summaries for clients that don't have them yet
      clients.forEach((client) => {
        if (!client.summary) {
          loadClientSummary(client.id);
        }
      });
    }
  }, [clients.length]); // Only run when clients array length changes

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load only clients with active cases (not approved, rejected, closed, or cancelled) for both dropdown and main panel
      const activeClientsData = await apiClient.getClientsWithActiveCases();

      // Load active clients first (without summaries)
      console.log(`📋 Loaded ${activeClientsData.length} active clients`);
      const clientsWithSummaries = activeClientsData.map((client: Client) => ({
        ...client,
        summary: undefined,
      }));
      setClients(clientsWithSummaries);
      setActiveClients(activeClientsData || []);

      // Then load summaries only for clients that have them (lazy loading)
      console.log("🔄 Loading summaries for clients that have them...");

      // For now, we'll load summaries on-demand when needed
      // This prevents the API errors for clients without summaries
      setActiveClients(activeClientsData || []);
    } catch (error) {
      console.error("Error loading clients:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load clients. Please try again.";
      setError(errorMessage);
    } finally {
      // Add a small artificial delay (100ms) to make the spinner more noticeable
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  // New function to properly refresh clients and their summaries
  const refreshClientsAndSummaries = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔄 Refreshing clients and summaries...");

      // Load clients with active cases
      const activeClientsData = await apiClient.getClientsWithActiveCases();
      console.log(`📋 Loaded ${activeClientsData.length} active clients`);

      // Set clients without summaries first
      const clientsWithSummaries = activeClientsData.map((client: Client) => ({
        ...client,
        summary: undefined,
      }));
      setClients(clientsWithSummaries);
      setActiveClients(activeClientsData || []);

      // Now load summaries for all clients
      console.log("🔄 Loading summaries for all clients...");
      for (const client of activeClientsData) {
        try {
          const summary = await apiClient.getClientSummary(client.id);
          if (summary) {
            console.log(
              `✅ Loaded summary for client ${client.id}:`,
              summary.generation_status
            );
            // Update the specific client with their summary
            setClients((prevClients) =>
              prevClients.map((c) =>
                c.id === client.id ? { ...c, summary } : c
              )
            );
          }
        } catch (error) {
          console.log(`❌ No summary found for client ${client.id}`);
          // Don't update state if no summary found
        }
      }

      console.log("✅ Refresh completed");
    } catch (error) {
      console.error("Error refreshing clients:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to refresh clients. Please try again.";
      setError(errorMessage);
    } finally {
      // Add a small artificial delay (100ms) to make the spinner more noticeable
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedClient) {
      setError("Please select a client first.");
      return;
    }

    try {
      console.log(
        "🚀 Starting client summary generation for client:",
        selectedClient
      );
      setGeneratingSummary(selectedClient);
      setError(null);
      setSuccess(null);

      console.log("📡 Calling API...");
      const result = await apiClient.generateClientSummary(selectedClient);
      console.log("✅ API Response:", result);

      const message =
        result.data?.message || "Client summary generation completed";
      setSuccess(
        `${message} for ${getClientName(selectedClient)}! Summary ID: ${
          result.data?.summaryId
        }`
      );

      // Show processing notification
      setSuccess(
        `🔄 Client summary is being generated for ${getClientName(
          selectedClient
        )}. This may take a few moments. You can refresh the page or click the refresh button to check the status.`
      );
      setPollingSummary(selectedClient);
    } catch (error) {
      console.error("❌ Error generating summary:", error);
      const apiError = error as any;
      const status = apiError?.response?.status;
      const data = apiError?.response?.data || {};
      const serverMessage =
        data?.message ||
        (error instanceof Error
          ? error.message
          : "Failed to generate client summary. Please try again.");
      const usageDetails =
        typeof data?.current === "number" && typeof data?.limit === "number"
          ? ` (${data.current}/${data.limit} summaries used)`
          : "";

      if (status === 403) {
        setError(`${serverMessage}${usageDetails}`);
      } else {
        setError(serverMessage);
      }
    } finally {
      setGeneratingSummary(null);
    }
  };
  const handleDownloadSummary = async (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client?.summary) {
      setError("No summary available for download.");
      return;
    }

    try {
      setDownloadingSummary(clientId);
      setError(null);

      const blob = await apiClient.downloadClientSummary(client.summary.id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `client-summary-${client.first_name}-${client.last_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(
        `Client summary downloaded successfully for ${getClientName(clientId)}!`
      );
    } catch (error) {
      console.error("Error downloading summary:", error);
      setError("Failed to download client summary. Please try again.");
    } finally {
      setDownloadingSummary(null);
    }
  };

  const getClientName = (clientId: string) => {
    // Check both activeClients and clients arrays
    const client =
      activeClients.find((c) => c.id === clientId) ||
      clients.find((c) => c.id === clientId);
    return client
      ? `${client.first_name} ${client.last_name}`
      : "Unknown Client";
  };

  const getSummaryStatus = (summary?: ClientSummary) => {
    if (!summary) {
      return { status: "pending", text: "Pending", color: "gray" };
    }

    // Only show 4 specific statuses as requested
    if (summary.status) {
      switch (summary.status) {
        case "case_created":
          return { status: "case-created", text: "Auto case created", color: "green" };
        case "case_creation_failed":
          return { status: "case-creation-failed", text: "Case creation failure", color: "red" };
        default:
          // For any other status (pending, reviewed, contacted), show as Unknown
          return { status: "unknown", text: "Unknown", color: "gray" };
      }
    }

    // Check OCR generation status for "Ready to download"
    if (summary.generation_status === "completed") {
      return { status: "completed", text: "Ready to download", color: "green" };
    }

    // Everything else shows as Unknown
    return { status: "pending", text: "Pending", color: "gray" };
  };

  // Load summary for a specific client
  const loadClientSummary = async (clientId: string) => {
    try {
      console.log(`🔍 Loading summary for client: ${clientId} (v2)`);
      const summary = await apiClient.getClientSummary(clientId);

      if (summary) {
        console.log(`✅ Summary loaded for client ${clientId}:`, {
          id: summary.id,
          status: summary.generation_status,
        });

        // Update the client with the summary
        setClients((prevClients) => {
          const updatedClients = prevClients.map((client) =>
            client.id === clientId ? { ...client, summary } : client
          );
          console.log(
            `🔄 Updated clients state for ${clientId}:`,
            updatedClients.find((c) => c.id === clientId)?.summary
              ?.generation_status
          );
          return updatedClients;
        });
      }
    } catch (error) {
      console.log(`❌ No summary found for client ${clientId}`);
      // Don't update state if no summary found
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="clients-container">
        <div
          className="clients-header"
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.9) 100%)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(229, 231, 235, 0.3)",
            padding: "20px 24px",
            borderRadius: "16px",
            marginBottom: "30px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <h2
            style={{
              margin: "0",
              color: "#000000",
              fontSize: "28px",
              fontWeight: "700",
            }}
          >
            Clients
          </h2>
        </div>
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.9) 100%)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(229, 231, 235, 0.3)",
            borderRadius: "16px",
            padding: "40px",
            textAlign: "center",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <p style={{ color: "#10b981", fontSize: "18px", fontWeight: "600" }}>
            Loading clients...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="clients-container">
      <div
        className="clients-header"
        style={{
          background:
            "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(229, 231, 235, 0.3)",
          padding: "20px 24px",
          borderRadius: "16px",
          marginBottom: "30px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        }}
      >
        <h2
          style={{
            margin: "0",
            color: "#000000",
            fontSize: "28px",
            fontWeight: "700",
          }}
        >
          Clients
        </h2>
        <div className="clients-actions">
          <button
            onClick={refreshClientsAndSummaries}
            className="refresh-btn"
            title="Refresh client list and summaries"
          >
            {React.createElement(FiRefreshCw as any)} Refresh
          </button>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="client-select"
            disabled={activeClients.length === 0}
          >
            <option value="">
              {activeClients.length === 0
                ? "No clients with active cases found"
                : "Select a client with active cases..."}
            </option>
            {activeClients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.first_name} {client.last_name} ({client.email})
              </option>
            ))}
          </select>

          <button
            onClick={handleGenerateSummary}
            disabled={!selectedClient || generatingSummary === selectedClient}
            className="generate-summary-btn"
          >
            {generatingSummary === selectedClient ? (
              <>
                <span className="spinner-small"></span>
                Generating...
              </>
            ) : (
              "Generate Client Summary"
            )}
          </button>
        </div>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="message error">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)} className="close-btn">
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="message success">
          <span>✅ {success}</span>
          <button onClick={() => setSuccess(null)} className="close-btn">
            ×
          </button>
        </div>
      )}

      {/* Clients List - Shows all clients with their summary status */}
      <div className="clients-table-container">
        {clients.length === 0 ? (
          <div className="no-clients">
            <p>
              No clients with active cases found. Only clients with active cases
              are displayed here.
            </p>
          </div>
        ) : (
          <div className="clients-table">
            <div className="table-header">
              <div className="header-cell name-cell">Client Name</div>
              <div className="header-cell email-cell">Email</div>
              <div className="header-cell summary-cell">Summary Status</div>
              <div className="header-cell actions-cell">Actions</div>
            </div>

            <div className="table-body">
              {clients.map((client) => {
                const summaryStatus = getSummaryStatus(client.summary);

                // Debug: Log what we're rendering for each client
                if (client.first_name === "shalini") {
                  console.log(`🎨 Rendering shalini (v2):`, {
                    hasSummary: !!client.summary,
                    summaryStatus: summaryStatus.text,
                    generationStatus: client.summary?.generation_status,
                    rawSummary: client.summary,
                    allKeys: client.summary ? Object.keys(client.summary) : [],
                  });
                }

                return (
                  <div key={client.id} className="table-row">
                    <div className="table-cell name-cell">
                      <div className="client-name-info">
                        <div className="client-name">
                          {client.first_name} {client.last_name}
                        </div>
                        <div className="client-details">
                          {client.phone && <span>📞 {client.phone}</span>}
                          {client.nationality && (
                            <span>🌍 {client.nationality}</span>
                          )}
                          {client.date_of_birth && (
                            <span>
                              🎂{" "}
                              {new Date(
                                client.date_of_birth
                              ).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="table-cell email-cell">
                      <span className="client-email">{client.email}</span>
                    </div>

                    <div className="table-cell summary-cell">
                      <div className="summary-status-info">
                        <span
                          className={`status-indicator ${summaryStatus.status}`}
                        >
                          {summaryStatus.text}
                        </span>
                        {client.summary?.generation_completed_at && (
                          <div className="summary-date">
                            {formatDate(client.summary.generation_completed_at)}
                          </div>
                        )}
                      </div>
                    </div>



                    <div className="table-cell actions-cell">
                      <div className="summary-actions">
                        {summaryStatus.status === "completed" &&
                          client.summary && (
                            <button
                              onClick={() => handleDownloadSummary(client.id)}
                              disabled={downloadingSummary === client.id}
                              className="download-summary-btn"
                            >
                              {downloadingSummary === client.id ? (
                                <>
                                  <span className="spinner-small"></span>
                                  Downloading...
                                </>
                              ) : (
                                "📥 Download"
                              )}
                            </button>
                          )}

                        {summaryStatus.status === "failed" &&
                          client.summary?.error_message && (
                            <div className="error-details">
                              <span>Error: {client.summary.error_message}</span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
